import React from "react";

export const DEFAULT_ROLE_MATCHING_STRENGTH = 50;
export const ROLE_TARGET_WEIGHT_SCALE = 4;
export const DEFAULT_SEARCH_EFFORT = "standard";
export const DEFAULT_OPENING_CLOSING_STRENGTH = 50;

export function openingClosingStrengthToWeight(strength) {
  return strength / 100;
}

export function roleMatchingStrengthToWeight(strength) {
  return strength * ROLE_TARGET_WEIGHT_SCALE;
}

export default function AutoScheduleOptimizationSettings({
  roleMatchingStrength,
  setRoleMatchingStrength,
  searchEffort,
  setSearchEffort,
  openingClosingStrength = DEFAULT_OPENING_CLOSING_STRENGTH,
  setOpeningClosingStrength,
  allowOpenShifts = false,
  setAllowOpenShifts,
  enforceOpeningClosingStaffing = false,
  setEnforceOpeningClosingStaffing,
  compact = false,
}) {
  return (
    <div className={compact ? "space-y-6" : "p-6"}>
      <div>
        <div className="flex items-center justify-between gap-4">
          <label
            htmlFor="role-target-weight"
            className="text-sm font-medium text-slate-700"
          >
            Role matching strength
          </label>
          <span className="text-sm font-semibold text-violet-700">
            {roleMatchingStrength}
          </span>
        </div>
        <input
          id="role-target-weight"
          type="range"
          min="0"
          max="100"
          step="5"
          value={roleMatchingStrength}
          onChange={(event) =>
            setRoleMatchingStrength(Number(event.target.value))
          }
          className="mt-3 w-full accent-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
        />
        <div className="mt-1 flex justify-between text-xs text-slate-400">
          <span>Coverage first</span>
          <span>Roles first</span>
        </div>
        <p className="mt-2 text-xs leading-5 text-slate-500">
          Lower this when historical role data is not representative.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between gap-4">
          <label htmlFor="opening-closing-strength" className="text-sm font-medium text-slate-700">
            Opening/closing adherence
          </label>
          <span className="text-sm font-semibold text-violet-700">{openingClosingStrength}</span>
        </div>
        <input
          id="opening-closing-strength"
          type="range"
          min="0"
          max="100"
          step="5"
          value={openingClosingStrength}
          onChange={(event) => setOpeningClosingStrength?.(Number(event.target.value))}
          className="mt-3 w-full accent-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
        />
        <div className="mt-1 flex justify-between text-xs text-slate-400">
          <span>Off</span>
          <span>Strong</span>
        </div>
        <p className="mt-2 text-xs leading-5 text-slate-500">
          Prefer familiar opening and closing employees and shift times from past schedules.
        </p>
      </div>

      <div>
        <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
          <input type="checkbox" checked={enforceOpeningClosingStaffing}
            onChange={(event) => setEnforceOpeningClosingStaffing?.(event.target.checked)}
            className="h-4 w-4 accent-violet-700 focus-visible:ring-2 focus-visible:ring-violet-600" />
          Enforce opening/closing staffing standards
        </label>
        <p className="mt-2 text-xs leading-5 text-slate-500">
          Match opening and closing staffing to the counts and roles in your labor model.
        </p>
      </div>

      <div>
        <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
          <input type="checkbox" checked={allowOpenShifts}
            onChange={(event) => setAllowOpenShifts(event.target.checked)}
            className="h-4 w-4 accent-violet-700 focus-visible:ring-2 focus-visible:ring-violet-600" />
          Allow open shifts
        </label>
        <p className="mt-2 text-xs leading-5 text-slate-500">
          Autoschedule can add unassigned shifts when available employees cannot cover demand.
        </p>
      </div>

      <div>
        <p className="text-sm font-medium text-slate-700">Search depth</p>
        <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-1">
          {[
            ["quick", "Quick"],
            ["standard", "Standard"],
            ["thorough", "Thorough"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={searchEffort === value}
              onClick={() => setSearchEffort(value)}
              className={`min-h-[40px] rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 ${
                searchEffort === value
                  ? "border-violet-600 bg-violet-50 text-violet-700"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs leading-5 text-slate-500">
          Quick returns results faster. Thorough explores more options.
        </p>
      </div>
    </div>
  );
}
